import { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, Package, DollarSign, Settings, 
  Menu, X, Bell, LogOut, ChevronDown, MapPin, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { toast } from 'sonner';

const menuItems = [
  { path: '/driver', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/driver/deliveries', icon: Package, label: 'Deliveries' },
  { path: '/driver/earnings', icon: DollarSign, label: 'Earnings' },
  { path: '/driver/settings', icon: Settings, label: 'Settings' },
];

export default function DriverLayout() {
  const { user, logout, api } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAvailable, setIsAvailable] = useState(user?.is_available || false);

  const toggleAvailability = async () => {
    try {
      const response = await api.put('/drivers/toggle-availability');
      setIsAvailable(response.data.is_available);
      toast.success(response.data.is_available ? 'You are now available' : 'You are now offline');
    } catch (error) {
      toast.error('Failed to update availability');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 transition-transform duration-200`}>
        <div className="p-4 border-b border-slate-800">
          <Link to="/" className="flex items-center gap-2">
            <svg viewBox="0 0 100 100" className="w-10 h-10">
              <rect width="100" height="100" rx="20" fill="#F97316"/>
              <g fill="white">
                <rect x="18" y="25" width="4" height="50" rx="2"/>
                <rect x="25" y="25" width="3" height="20" rx="1.5"/>
                <rect x="18" y="25" width="3" height="20" rx="1.5"/>
                <rect x="11" y="25" width="3" height="20" rx="1.5"/>
                <ellipse cx="78" cy="35" rx="12" ry="15"/>
                <rect x="76" y="45" width="4" height="30" rx="2"/>
                <rect x="30" y="50" width="44" height="8" rx="4"/>
              </g>
            </svg>
            <div>
              <span className="font-bold text-lg block">Hambre</span>
              <span className="text-xs text-slate-400">Driver</span>
            </div>
          </Link>
        </div>

        {/* Availability Toggle */}
        <div className="p-4 border-b border-slate-800">
          <button
            onClick={toggleAvailability}
            className={`w-full flex items-center justify-between p-3 rounded-lg ${isAvailable ? 'bg-green-500/20' : 'bg-slate-800'}`}
            data-testid="availability-toggle"
          >
            <span className="flex items-center gap-2">
              <MapPin className={`w-5 h-5 ${isAvailable ? 'text-green-400' : 'text-slate-400'}`} />
              <span className={isAvailable ? 'text-green-400' : 'text-slate-400'}>
                {isAvailable ? 'Available' : 'Offline'}
              </span>
            </span>
            {isAvailable ? (
              <ToggleRight className="w-6 h-6 text-green-400" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-slate-400" />
            )}
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-4 left-4 right-4">
          <Button
            variant="ghost"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={logout}
          >
            <LogOut className="w-5 h-5 mr-3" />
            Logout
          </Button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top header */}
        <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(!sidebarOpen)}
              >
                {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
              <h1 className="text-lg font-semibold text-slate-900">Driver Dashboard</h1>
            </div>

            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="w-5 h-5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center">
                      <span className="text-sm font-semibold text-orange-600">
                        {user?.name?.charAt(0) || 'D'}
                      </span>
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                    <ChevronDown className="w-4 h-4 text-slate-400" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem asChild>
                    <Link to="/driver/settings">Settings</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} className="text-red-600">
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
